import { useEffect, useMemo, useState } from "react";
import { useMondayBoardGroups, shouldExcludeGroup, type MondayGroup } from "@/hooks/use-monday-import";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";

interface Props {
  boardId: number | null;
  selectedGroupIds: string[];
  onChange: (groupIds: string[]) => void;
}

/**
 * Lar admin velge hvilke Monday-grupper som skal inkluderes i import.
 * Auto-filter ekskluderer "intern", "tilbud", "pitch" osv. ved første lasting,
 * men admin kan overstyre per gruppe.
 */
export function ImportGroupSelector({ boardId, selectedGroupIds, onChange }: Props) {
  const { data: groups = [], isLoading, error } = useMondayBoardGroups(boardId);
  const [autoFilterApplied, setAutoFilterApplied] = useState(false);

  // Auto-velg grupper som ikke matcher ekskluderings-listen, første gang
  // gruppene lastes for et nytt board.
  useEffect(() => {
    if (groups.length > 0 && !autoFilterApplied) {
      const included = groups.filter((g) => !shouldExcludeGroup(g.title)).map((g) => g.id);
      onChange(included);
      setAutoFilterApplied(true);
    }
  }, [groups, autoFilterApplied, onChange]);

  // Reset auto-filter-flagg når boardId endres
  useEffect(() => {
    setAutoFilterApplied(false);
  }, [boardId]);

  const partitioned = useMemo(() => {
    const included: MondayGroup[] = [];
    const excluded: MondayGroup[] = [];
    for (const g of groups) {
      if (shouldExcludeGroup(g.title)) excluded.push(g);
      else included.push(g);
    }
    return { included, excluded };
  }, [groups]);

  const toggle = (groupId: string) => {
    if (selectedGroupIds.includes(groupId)) {
      onChange(selectedGroupIds.filter((id) => id !== groupId));
    } else {
      onChange([...selectedGroupIds, groupId]);
    }
  };

  const selectAll = () => onChange(groups.map((g) => g.id));
  const selectNone = () => onChange([]);
  const selectIncluded = () => onChange(partitioned.included.map((g) => g.id));

  if (!boardId) {
    return <p className="text-sm text-muted-foreground">Velg en ansatt først.</p>;
  }
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <SrIcon name="loader2" className="w-4 h-4 animate-spin" />
        Henter grupper fra Monday...
      </div>
    );
  }
  if (error) {
    return (
      <p className="text-sm text-destructive">
        Kunne ikke hente grupper: {(error as Error).message}
      </p>
    );
  }
  if (groups.length === 0) {
    return <p className="text-sm text-muted-foreground">Ingen grupper på dette boardet.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs">
        <button
          type="button"
          onClick={selectIncluded}
          className="px-2 py-1 rounded border hover:bg-muted/50"
        >
          Bare relevante
        </button>
        <button
          type="button"
          onClick={selectAll}
          className="px-2 py-1 rounded border hover:bg-muted/50"
        >
          Alle
        </button>
        <button
          type="button"
          onClick={selectNone}
          className="px-2 py-1 rounded border hover:bg-muted/50"
        >
          Ingen
        </button>
        <span className="ml-auto text-muted-foreground tabular-nums">
          {selectedGroupIds.length} av {groups.length} valgt
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-72 overflow-y-auto border rounded-md p-2">
        {[...partitioned.included, ...partitioned.excluded].map((g) => {
          const isExcluded = shouldExcludeGroup(g.title);
          const isSelected = selectedGroupIds.includes(g.id);
          return (
            <label
              key={g.id}
              className={cn(
                "flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm hover:bg-muted/40",
                isExcluded && !isSelected && "opacity-50",
              )}
            >
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggle(g.id)}
                className="rounded"
              />
              <span className="flex-1 truncate">{g.title}</span>
              {isExcluded && (
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  intern
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}
